import type React from 'react';
import { useEffect, useRef, useState } from 'react';

const LazyLoad = ({
  children,
  threshold = 0.0,
  rootMargin = '50px',
  height = '100px',
  width = '100px',
  placeholderClassname = '',
  customPlaceholder = null,
  customId = '',
}: {
  children: React.ReactNode;
  threshold?: number;
  rootMargin?: string;
  height?: string;
  width?: string;
  placeholderClassname?: string;
  customPlaceholder?: React.ReactNode;
  customId?: string;
}) => {
  // React refs
  const observer = useRef<IntersectionObserver | null>(null);
  const el = useRef<HTMLDivElement | null>(null);

  // React state
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const options = {
      root: null,
      rootMargin,
      threshold,
    };

    if (window) {
      observer.current = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting) {
          if (el.current && observer.current) {
            observer.current.unobserve(el.current);
          }
          setIsVisible(true);
        }
      }, options);

      // Start observing
      if (el.current) {
        observer.current.observe(el.current);
      }
    }

    return () => {
      if (observer.current) {
        observer.current.disconnect();
      }
    };
  }, [rootMargin, threshold]);

  const style = {
    height,
    width,
  };

  return isVisible ? (
    children
  ) : (
    <div ref={el} style={style} id={customId} className={placeholderClassname}>
      {customPlaceholder}
    </div>
  );
};

export default LazyLoad;
